import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { Textarea } from '../../../../components/inputs/Textarea';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdInput, TdNumber, TdSeamless, TdSelect } from '../../../../components/inline-add-table/cells';
import { ApiError } from '../../../../lib/apiError';
import { itemIdentityOrName } from '../../../../lib/itemIdentity';
import { focusValidationError } from '../../../../lib/validationFocus';
import { usePermission } from '../../../../auth/AuthContext';
import { storageFacilitiesApi } from '../../../logistics/inventory/inventoryApi';
import type { StorageFacilityOption } from '../../../logistics/inventory/inventoryTypes';
import { personnelApi, personsApi } from '../../../human-resources/personnel/personnelApi';
import { requisitionSlipsApi } from '../requisitionSlipsApi';
import type {
  CurrentRequester,
  RequestableItem,
  RequisitionSlipDetail,
  RequisitionSlipLineInput,
} from '../requisitionSlipTypes';
import { sampleRequisitionSlip } from '../utils/sampleRequisitionSlip';

const ID_PREFIX = 'requisition-slip';

/**
 * Raise or correct a requisition slip.
 *
 * **ONE PANEL, not a stepper.** What is left after the requesting office is derived away is a
 * store, an accountable employee, a purpose and the lines — a rail over that would promise
 * stages of work that are three fields, which is the call that took the Identity step off the
 * item form.
 *
 * **THE REQUESTING OFFICE IS STATED, NEVER ASKED.** It is derived server-side from the
 * requester's own personnel record, and the request carries no field for it: a value a caller
 * can name is a value a caller can misname onto a signed slip.
 */
export function RequisitionSlipModal({
  editing,
  onClose,
  onSaved,
}: {
  /** The slip being corrected, or undefined to raise one. */
  editing?: RequisitionSlipDetail;
  onClose: () => void;
  onSaved: (saved: RequisitionSlipDetail) => void;
}) {
  const isEdit = editing !== undefined;

  const [facilityId, setFacilityId] = useState<string | null>(editing?.storageFacilityId ?? null);
  const [accountableId, setAccountableId] = useState<string | null>(
    editing?.accountablePersonnelId ?? null,
  );
  const [purpose, setPurpose] = useState(editing?.purpose ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');

  /*
    NORMALISE AT THE SEAM, where a wire row becomes a form row. A nullable field arrives as
    `undefined` rather than null, so `?? null` here is what makes the row type's promise true
    and keeps every comparison downstream from having to know about the serializer.
  */
  const [rows, setRows] = useState<RequisitionSlipLineInput[]>(
    editing?.items.map((line) => ({
      inventoryItemId: line.inventoryItemId,
      requestedQuantity: line.requestedQuantity,
      remarks: line.remarks ?? null,
    })) ?? [],
  );

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const [items, setItems] = useState<RequestableItem[]>([]);
  const [facilities, setFacilities] = useState<StorageFacilityOption[]>([]);
  const [requester, setRequester] = useState<CurrentRequester | null>(null);
  const [staff, setStaff] = useState<{ id: string; name: string; unit: string | null }[]>([]);

  const canReadPersonnel = usePermission('personnel.read');

  useEffect(() => {
    const controller = new AbortController();

    /*
      THE FEEDS FAIL SOFT. A failed picker load leaves the form working and the rest of the
      page intact rather than failing the whole dialog — the repository share dialog's own
      call. A failure here is not a failure of the SAVE path, so it must not reach the banner
      the save uses, and `focusValidationError` is deliberately not called from this catch.
    */
    void requisitionSlipsApi
      .requestableItems(controller.signal)
      .then(setItems)
      .catch(() => undefined);

    void storageFacilitiesApi
      .options(controller.signal)
      .then(setFacilities)
      .catch(() => undefined);

    void requisitionSlipsApi
      .me(controller.signal)
      .then(setRequester)
      .catch(() => undefined);

    return () => controller.abort();
  }, []);

  /*
    THE ACCOUNTABLE PICKER resolves its NAMES through the identity service, in ONE batch call
    for the page it is showing — never one request per row. That is the whole reason a
    personnel row carries a `personId` rather than a name, and it is the issuance form's own
    call, copied rather than reinvented.

    It is gated on `personnel.read`, which this module declares as a SCOPED implied read for
    create and update — so the grant exists precisely to feed this control.
  */
  useEffect(() => {
    if (!canReadPersonnel) return;

    const controller = new AbortController();

    void personnelApi
      .list({ pageSize: 100 }, controller.signal)
      .then(async (page) => {
        const names = await personsApi.resolve(
          // A record above this reader's access level arrives with NO personId — that absence
          // IS the mask — so there is nothing to resolve, and the option falls back to the
          // employee number, which identifies the row without naming the person.
          page.items.map((p) => p.personId).filter((id): id is string => id != null),
          controller.signal,
        );

        if (controller.signal.aborted) return;

        const byPersonId = new Map(names.map((n) => [n.personId, n.displayName]));

        setStaff(
          page.items.map((p) => ({
            id: p.id,
            name: (p.personId != null ? byPersonId.get(p.personId) : undefined) ?? p.employeeNumber,
            unit: p.orgUnitName ?? null,
          })),
        );
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [canReadPersonnel]);

  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  const update = useCallback((index: number, patch: Partial<RequisitionSlipLineInput>) => {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }, []);

  const liveRows = rows.filter((row) => row.inventoryItemId !== '');

  const canSave =
    purpose.trim().length > 0 && liveRows.length > 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const body = {
        storageFacilityId: facilityId,
        accountablePersonnelId: accountableId,
        purpose: purpose.trim(),
        notes: notes.trim() || null,
        items: rows,
      };

      const saved = isEdit
        ? await requisitionSlipsApi.update(editing.id, body)
        : await requisitionSlipsApi.create(body);

      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this slip.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID_PREFIX });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `Edit ${editing.slipNumber}` : 'Raise a requisition slip'}
      size="3xl"
      id={`${ID_PREFIX}-modal`}
      confirm={{
        id: `${ID_PREFIX}-confirm`,
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      autogenerate={{
        onGenerate: () => {
          const generated = sampleRequisitionSlip({ items, facilities, staff });

          /*
            NEVER A FIELD THE READER HAS ALREADY DECIDED — a line already picked keeps its
            item, its quantity and its remark. The generator fills what is empty.
          */
          if (!purpose.trim()) setPurpose(generated.purpose);
          if (!notes.trim()) setNotes(generated.notes);
          if (facilityId === null) setFacilityId(generated.storageFacilityId);
          if (accountableId === null) setAccountableId(generated.accountablePersonnelId);
          if (liveRows.length === 0) setRows(generated.items);
        },
        disabled: saving || items.length === 0,
      }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID_PREFIX}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          THE OFFICE IS STATED, and an account with no post is told so HERE rather than meeting
          a refusal on save. It fails SOFT: the message comes back on the DTO, so the rest of
          the form still works and a reader can see what they would have asked for.
        */}
        {requester?.error != null ? (
          <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs text-amber-800">{requester.error}</p>
          </div>
        ) : (
          <p className="text-xs text-slate-600">
            Raised for{' '}
            <strong className="text-slate-800">{requester?.orgUnitName ?? 'your office'}</strong>,
            read from your own employee record. It is printed on the slip and is where the
            supplies are delivered.
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id={`${ID_PREFIX}-storage-facility-id`}
            label="Store asked"
            description="Which store is being asked to fill this. It is what routes the slip."
            value={facilityId}
            onChange={setFacilityId}
            options={facilities.map((f) => ({ value: f.id, label: f.name, sublabel: f.code }))}
            remote
            error={error?.fieldError('storageFacilityId')}
            disabled={saving}
          />

          {/*
            GATED ON `personnel.read`, and it HIDES rather than disabling — a reader without
            the key is not blocked by state, they simply have no business naming an employee.
            The field is optional, so the form stays submittable without it.
          */}
          {canReadPersonnel ? (
            <Select
              id={`${ID_PREFIX}-accountable-personnel-id`}
              label="Accountable employee"
              description="Who will answer for the issued property. Captured now so the issuance does not have to ask again."
              value={accountableId}
              onChange={setAccountableId}
              options={staff.map((s) => ({
                value: s.id,
                label: s.name,

                // THE UNIT under the name — a list of display names alone stops working the
                // moment two employees share a surname.
                sublabel: s.unit ?? undefined,
              }))}
              remote
              error={error?.fieldError('accountablePersonnelId')}
              disabled={saving}
            />
          ) : null}
        </div>

        <InlineAddTable
          headers={[
            'Item *',
            'Unit',
            { label: 'Available', align: 'right' },
            { label: 'Qty *', align: 'right' },
            'Remarks',
          ]}
          colWidths={[320, 90, 120, 100, 240]}
          resizeKey={`${ID_PREFIX}-lines-v1`}
          rowCount={rows.length}
          onAddRow={() =>
            setRows((current) => [
              ...current,
              { inventoryItemId: '', requestedQuantity: 1, remarks: null },
            ])
          }
          addDisabled={saving}
          addButtonId={`${ID_PREFIX}-add-line`}
          footerContent={
            /*
              SHORT, because the footer sits inside the grid's own horizontal scroll region and
              a long sentence is CLIPPED rather than wrapped.

              IT SAYS THE UNBOUNDED RULE OUT LOUD, because it is the one a reader will assume
              the other way: asking for more than is on the shelf is a supported submission,
              and the gap is what Logistics restocks against.
            */
            <p className="text-xs text-slate-600">
              Ask for what the office needs — more than the available figure is fine, and the
              shortfall is what Logistics restocks against.
            </p>
          }
        >
          {rows.map((row, index) => {
            const picked = row.inventoryItemId ? itemsById.get(row.inventoryItemId) : undefined;

            return (
              <InlineRow
                key={index}
                idx={index + 1}
                onRemove={() => setRows((current) => current.filter((_, i) => i !== index))}
              >
                <TdSelect
                  id={`${ID_PREFIX}-items.${index}.inventoryItemId`}
                  ariaLabel={`Item on line ${index + 1}`}
                  value={row.inventoryItemId || null}
                  onChange={(next) => update(index, { inventoryItemId: next })}
                  /*
                    THE LABEL IS THE PRODUCT, through `lib/itemIdentity` — an item has no name
                    column of its own, so a catalog holding three laptops would otherwise offer
                    "Laptop" three times and the reader could only tell them apart by a SKU
                    nobody has memorised. The SKU leaves the label and stays SEARCHABLE.
                  */
                  options={items.map((i) => ({
                    value: i.id,
                    label: itemIdentityOrName(i, i.itemName ?? i.sku),
                    sublabel: i.itemName ?? undefined,
                    keywords: i.sku,
                  }))}
                  remote
                  disabled={saving}
                />

                {/* The unit is a LIVE view off the item, never copied onto the line. */}
                <TdSeamless align="left">{picked?.unitOfMeasure ?? '—'}</TdSeamless>

                {/*
                  ADVISORY, and deliberately not a cap. It is what the office did not have when
                  they asked, which is the figure that makes the request readable — not a
                  ceiling the line is held to.
                */}
                <TdSeamless align="right">
                  {picked ? String(picked.availableQuantity) : '—'}
                </TdSeamless>

                <TdNumber
                  id={`${ID_PREFIX}-items.${index}.requestedQuantity`}
                  ariaLabel={`Quantity on line ${index + 1}`}
                  value={row.requestedQuantity}
                  onChange={(next) => update(index, { requestedQuantity: next ?? 0 })}
                  min={1}
                  disabled={saving}
                />

                <TdInput
                  id={`${ID_PREFIX}-items.${index}.remarks`}
                  ariaLabel={`Remarks on line ${index + 1}`}
                  value={row.remarks ?? ''}
                  onChange={(next) => update(index, { remarks: next || null })}
                  placeholder="optional"
                  disabled={saving}
                />
              </InlineRow>
            );
          })}
        </InlineAddTable>

        {/*
          PURPOSE and NOTES are FULL WIDTH, outside the grid — the remarks rule. Each is the
          longest value on the form and the one most likely to be a sentence, so half a row is
          the box that truncates while the field beside it needed none of the room.
        */}
        <Textarea
          id={`${ID_PREFIX}-purpose`}
          label="Purpose"
          required
          description="Why the office needs these supplies now. It is what an approver reads first."
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          error={purpose.trim() ? error?.fieldError('purpose') : 'Say why the office needs these.'}
          disabled={saving}
        />

        <Textarea
          id={`${ID_PREFIX}-notes`}
          label="Notes"
          description="Anything else the store should know."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          error={error?.fieldError('notes')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
